import React, { useEffect, useMemo, useState } from 'react'
import { AnimatePresence, motion } from 'motion/react'
import { api } from '../../api/client'
import PersonAvatar from '../PersonAvatar'
import PersonDetailModal, {
    PersonDetailValue,
} from '../checkin/PersonDetailModal'
import { PersonLikeData, formatPersonDisplayName } from '../../utils/people'
import ChartTimeRangePicker from '../../charts/ChartTimeRangePicker'
import { toLocalDateStr } from '../../utils/dates'
import {
    useTimeRangeState,
    windowForAnchor,
    isAllTime,
} from '../../charts/windowRange'

/**
 * Per-project People section. Shows the people tagged on this project's
 * checkins as an avatar grid, ordered by frequency (most → least), with the
 * same calendar-aligned time range picker the Places section and the charts
 * use (W/M/6M/Y/All). Refetches on window change — the backend does the
 * filtering against checkins.applies_to and the project_id, so the frontend
 * just hands it a half-open [start, end) range derived from the picker.
 *
 * Lifetime data-bearing dates come back on every response (independent of
 * the date filter) so the picker can skip empty windows on prev/next.
 *
 * Mirrors `ProjectPlacesSection` in shape; the only visual difference is an
 * avatar grid instead of a map, and no view-toggle (no "heatmap vs clusters"
 * analogue for people).
 */

interface ProjectPeopleSectionProps {
    projectId: number
    onLifetimeEmptyChange?: (empty: boolean) => void
}

interface ProjectPersonItem {
    collection_item_id: number
    data: PersonLikeData
    checkin_count: number
}

interface ProjectPeopleResponse {
    people: ProjectPersonItem[]
    checkin_dates: string[]
}

const ProjectPeopleSection: React.FC<ProjectPeopleSectionProps> = ({ projectId, onLifetimeEmptyChange }) => {
    const [people, setPeople] = useState<ProjectPersonItem[]>([])
    const [checkinDates, setCheckinDates] = useState<string[]>([])
    const [loading, setLoading] = useState(true)
    const [error, setError] = useState<string | null>(null)

    const [selected, setSelected] = useState<PersonDetailValue | null>(null)
    const [showAll, setShowAll] = useState(false)

    // Picker is data-aware: it needs every checkin date in scope to page
    // prev/next past empty windows. Seed dataDates from the lifetime
    // `checkin_dates` returned alongside the people list on every response.
    const dateMs = useMemo(
        () => checkinDates.map((d) => Date.parse(d)).filter((n) => !Number.isNaN(n)),
        [checkinDates],
    )
    const range = useTimeRangeState(dateMs)
    const win = useMemo(
        () => windowForAnchor(range.granularity, range.anchor),
        [range.granularity, range.anchor],
    )

    useEffect(() => {
        let cancelled = false
        const params = new URLSearchParams()
        params.set('project_id', String(projectId))
        if (!isAllTime(range.granularity)) {
            params.set('start_date', toLocalDateStr(win.start))
            params.set('end_date', toLocalDateStr(win.end))
        }
        setLoading(true)
        setShowAll(false)
        api.get<ProjectPeopleResponse>(`/api/people/by_project?${params.toString()}`)
            .then((resp) => {
                if (cancelled) return
                setPeople(resp.data.people)
                setCheckinDates(resp.data.checkin_dates || [])
                setError(null)
            })
            .catch((err) => {
                if (cancelled) return
                console.error('Failed to load project people:', err)
                setError('Failed to load people.')
            })
            .finally(() => {
                if (!cancelled) setLoading(false)
            })
        return () => {
            cancelled = true
        }
    }, [projectId, range.granularity, win.start, win.end])

    // Lifetime-empty: no people tagged on the project's checkins at all.
    // Distinct from "this window happens to be empty" — that one keeps the
    // picker visible so the user can page back.
    const lifetimeEmpty = !loading && checkinDates.length === 0

    useEffect(() => {
        if (loading) return
        onLifetimeEmptyChange?.(lifetimeEmpty)
    }, [loading, lifetimeEmpty, onLifetimeEmptyChange])

    const PEOPLE_COLLAPSED_COUNT = 5
    const visiblePeople = showAll
        ? people
        : people.slice(0, PEOPLE_COLLAPSED_COUNT)
    const hiddenCount = people.length - PEOPLE_COLLAPSED_COUNT

    return (
        <div>
            <div className="flex items-center justify-between mb-3 gap-2">
                <h2 className="text-2xl font-semibold text-gray-800">People</h2>
            </div>

            {lifetimeEmpty ? (
                <div className="text-gray-500 text-sm py-8 text-center">
                    No people tagged on this project's checkins yet.
                </div>
            ) : (
                <>
                    <ChartTimeRangePicker state={range} />
                    {error ? (
                        <div className="text-rose-500 text-sm py-4">{error}</div>
                    ) : people.length === 0 && !loading ? (
                        <div className="text-gray-500 text-sm py-8 text-center">
                            No people tagged in this period.
                        </div>
                    ) : (
                        <>
                            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
                                <AnimatePresence initial={false}>
                                    {visiblePeople.map((p) => (
                                        <PersonCard
                                            key={p.collection_item_id}
                                            person={p}
                                            onOpen={() =>
                                                setSelected({
                                                    collectionItemId: p.collection_item_id,
                                                    data: p.data,
                                                })
                                            }
                                        />
                                    ))}
                                </AnimatePresence>
                            </div>
                            {hiddenCount > 0 && (
                                <div className="mt-3 text-center">
                                    <motion.button
                                        type="button"
                                        onClick={() => setShowAll((v) => !v)}
                                        whileHover={{ scale: 1.05 }}
                                        whileTap={{ scale: 0.95 }}
                                        transition={{ type: 'spring', stiffness: 400, damping: 20 }}
                                        className="text-xs text-amber-800 hover:text-amber-900 px-2 py-1 cursor-pointer"
                                    >
                                        {showAll ? 'Show fewer' : `Show all ${people.length}`}
                                    </motion.button>
                                </div>
                            )}
                        </>
                    )}
                </>
            )}

            <PersonDetailModal
                open={selected !== null}
                value={selected}
                onClose={() => setSelected(null)}
            />
        </div>
    )
}

const PersonCard: React.FC<{
    person: ProjectPersonItem
    onOpen: () => void
}> = ({ person, onOpen }) => {
    const name = formatPersonDisplayName(person.data)
    const relation =
        typeof person.data?.relation === 'string' ? person.data.relation.trim() : ''
    return (
        <motion.button
            type="button"
            layout
            style={{ transformOrigin: 'left center' }}
            initial={{ opacity: 0, scale: 0.6 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.6 }}
            transition={{ type: 'spring', stiffness: 500, damping: 30 }}
            whileHover={{ scale: 1.03 }}
            whileTap={{ scale: 0.97 }}
            onClick={onOpen}
            className="bg-white rounded-lg shadow-sm border border-gray-200 hover:border-sky-300 transition-colors p-3 flex flex-col items-center text-left"
            title={`View ${name}`}
        >
            <PersonAvatar
                data={person.data}
                className="h-16 w-16 mb-2"
                initialsClassName="text-lg"
                alt={name}
            />
            <div className="font-medium text-gray-900 text-center text-sm truncate w-full">
                {name}
            </div>
            {relation && (
                <div className="mt-1 inline-flex items-center px-2 py-0.5 rounded-full bg-sky-50 text-sky-700 text-xs truncate max-w-full">
                    {relation}
                </div>
            )}
            <div className="mt-1 text-xs text-gray-500">
                {person.checkin_count} checkin{person.checkin_count === 1 ? '' : 's'}
            </div>
        </motion.button>
    )
}

export default ProjectPeopleSection
